/* eslint-disable no-undef */
import React, { useState, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useAccount, useSwitchChain, useChainId } from 'wagmi'
import {
  readContract,
  writeContract,
  waitForTransactionReceipt
} from '@wagmi/core'
import Cookies from 'universal-cookie'
import Web3 from 'web3'
import ClipLoader from 'react-spinners/ClipLoader'
import { toast } from 'react-hot-toast'

import ThunderIcon from '../icons/thunder.svg'
import TwitterIcon from '../icons/x-icon.svg'
import TelegramIcon from '../icons/telegram.svg'
import WebsiteIcon from '../icons/website.svg'
import UserIcon from '../icons/user.svg'
import ChefIcon from '../icons/chef.svg'
import ChartIcon from '../icons/graph.svg'
import ArrowUpIcon from '../icons/arrow-up.svg'
import ArrowDownIcon from '../icons/arrow-down.svg'
import StarIcon from '../icons/star.svg'
import StarlineIcon from '../icons/star-line.svg'
import UsersIcon from '../icons/users.svg'

import ChadAbi from '../config/ChadPumpAbi.json'
import ChadAbiV2 from '../config/ChadPumpAbiV2.json'
import rot13 from '../../utils/encode.ts'
import { getDefaultAddress } from '../utils/addressHelpers.ts'
import { wagmiAdapter } from '../App.jsx'
import TokenAbi from '../config/TokenAbi.json'
import InfoAbi from '../config/InfoAbi.json'
import { getInfoAddress } from '../utils/addressHelpers.ts'
import TokenLogoImage from './TokenLogoImage.jsx'

// Money: K/M/B with 2 fixed decimals (match Top 10)
const formatMoney1 = n => {
  const x = Number(n) || 0
  const abs = Math.abs(x)
  let v = x
  let s = ''
  if (abs >= 1e9) {
    v = x / 1e9
    s = 'B'
  } else if (abs >= 1e6) {
    v = x / 1e6
    s = 'M'
  } else if (abs >= 1e3) {
    v = x / 1e3
    s = 'K'
  }
  const str = Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return str + s
}

const LaunchpadCard = ({
  apiUrl,
  coinNames,
  buyAmount,
  startTime,
  chainId,
  progress,
  tokenName,
  tokenSymbol,
  logoUrl,
  chadAddress,
  depositedAmount,
  contractAddress,
  tokenAddress,
  marketCap,
  website,
  twitter,
  telegram,
  BlockchainLogo,
  blockchainLogoUrl,
  BlockchainLogoUrl,
  lpCreated,
  watchlist,
  toggleWatchlist,
  lastAction,
  lastActionNumber,
  top15HoldersPercent,
  devPercent,
  holdersNumber,
  description,
  isRising,
  recentBuzz = false,
  versionType
}) => {
  const { address } = useAccount()
  const history = useHistory()
  const [creating, setCreating] = useState(false)
  const [justUpdated, setJustUpdated] = useState(false)
  const cookies = useMemo(() => new Cookies(), [])
  const resolvedLogoUrl = useMemo(
    () => logoUrl || '',
    [logoUrl]
  )
  const resolvedChainLogoUrl = useMemo(
    () => blockchainLogoUrl || BlockchainLogoUrl || '',
    [blockchainLogoUrl, BlockchainLogoUrl]
  )

  const desc = useMemo(
    () => (description ? String(description).trim() : ''),
    [description]
  )

  const normalizedVersion = useMemo(
    () => (versionType ? String(versionType).toLowerCase() : ''),
    [versionType]
  )
  const isLegacyV2 = normalizedVersion === 'v2'
  const isV4 = normalizedVersion === 'v4'
  const contractAbi = isLegacyV2 ? ChadAbiV2 : ChadAbi
  const cardDisabled = !chadAddress || !chainId

  const {
    switchChain,
    isLoading: isSwitching,
    error: switchError
  } = useSwitchChain()
  const currentChainId = useChainId()

  const navigateTo = useMemo(() => {
    const params = new URLSearchParams()
    if (chainId !== undefined && chainId !== null)
      params.set('chain', String(chainId))
    if (chadAddress) params.set('address', chadAddress)
    if (normalizedVersion) params.set('version', normalizedVersion)
    return `/buy/?${params.toString()}`
  }, [chainId, chadAddress, normalizedVersion])

  const refAddress = useMemo(() => {
    const ref = cookies.get('ref')
    if (ref && Web3.utils.isAddress(rot13(ref))) return rot13(ref)
    return getDefaultAddress()
  }, [cookies])

  const getTimeAgo = timestamp => {
    const now = Date.now()
    const diff = now - timestamp * 1000
    const seconds = Math.floor(diff / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.floor(hours / 24)}d`
  }

  const timeAgo = useMemo(() => getTimeAgo(startTime), [startTime])

  const onTokenSwap = useCallback(async () => {
    if (!buyAmount || Number(buyAmount) <= 0) {
      toast.error('Please enter a valid amount to swap.')
      return
    }
    if (!address) {
      toast.error('Please connect your wallet.')
      return
    }

    const balance = await window.ethereum.request({
      method: 'eth_getBalance',
      params: [address, 'latest']
    })
    const ethBalance = Number(Web3.utils.fromWei(balance, 'ether'))
    if (ethBalance < Number(buyAmount)) {
      toast.error(
        `You don't have enough ${coinNames[chainId]} to complete this trade.`
      )
      return
    }

    try {
      setCreating(true)

      if (chainId !== currentChainId) {
        await switchChain({ chainId })
        if (switchError)
          throw new Error(
            `Failed to switch to the correct network: ${switchError.message}`
          )
      }

      let tokenOutAmount = 0
      if (!isLegacyV2) {
        const amountOutRaw = await readContract(wagmiAdapter.wagmiConfig, {
          address: chadAddress,
          abi: ChadAbi,
          functionName: 'ethOrTokenAmount',
          args: [Web3.utils.toWei(buyAmount.toFixed(18), 'ether'), '1'],
          chainId
        })
        tokenOutAmount = Number(
          Web3.utils.fromWei(amountOutRaw.toString(), 'ether')
        )

        const tokenBalance = await readContract(wagmiAdapter.wagmiConfig, {
          address: tokenAddress,
          abi: TokenAbi,
          functionName: 'balanceOf',
          args: [address],
          chainId
        })
        const formattedBalance = Number(
          Web3.utils.fromWei(tokenBalance.toString(), 'ether')
        )

        const result = await readContract(wagmiAdapter.wagmiConfig, {
          address: getInfoAddress(chainId),
          abi: InfoAbi,
          functionName: 'getFunBasicInfo',
          args: [chadAddress],
          chainId
        })
        const totalSupply = Number(
          Web3.utils.fromWei(result[0][0].toString(), 'ether')
        )
        const maxLimitPercent = Number(result[0][2])
        const maxBuyAmount = (totalSupply * maxLimitPercent) / 100

        const excessAmount = tokenOutAmount + formattedBalance - maxBuyAmount
        if (excessAmount > 0) {
          toast.error(
            `This trade would put you over the max wallet limit by approximately ${excessAmount.toLocaleString()} ${tokenSymbol}. Reduce the amount and try again.`
          )
          setCreating(false)
          return
        }
      }

      const buyArgs = isV4 ? [refAddress, 0] : [refAddress]

      const swap = await writeContract(wagmiAdapter.wagmiConfig, {
        address: chadAddress,
        abi: contractAbi,
        functionName: 'buyToken',
        value: BigInt(Math.floor(buyAmount * 1e18)),
        args: buyArgs,
        account: address,
        gas: 350000,
        chainId
      })

      await waitForTransactionReceipt(wagmiAdapter.wagmiConfig, { hash: swap })

      await fetch(`${apiUrl}/api/addHistory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chainId,
          buyer: address,
          type: 'bought',
          name: tokenSymbol,
          amount: buyAmount,
          token: tokenAddress,
          contract: contractAddress,
          timestamp: Math.floor(Date.now() / 1000)
        })
      })
      setJustUpdated(true)
      setTimeout(() => setJustUpdated(false), 300)
      toast.success(`Swapped ${buyAmount} ${coinNames[chainId]}`)
    } catch (err) {
      console.error(err)
      if (!address) {
        toast.error('Please connect your wallet.')
        return
      }
      const message = err?.message || ''
      if (message.includes('insufficient funds')) {
        toast.error('you don’t have enough funds to complete this swap.')
      } else if (
        message.includes('InvalidCode') ||
        message.includes('revert')
      ) {
        toast.error('the contract execution failed.')
      } else if (message.includes('not in safe integer range')) {
        toast.error(
          'please enter a smaller amount. too many decimals or too large of a number.'
        )
      } else if (message.includes('User rejected')) {
        toast.error('transaction rejected by the user.')
      } else if (message.includes('network mismatch')) {
        toast.error('please switch to the correct network.')
      } else {
        toast.error('swap failed. try again later.')
      }
    } finally {
      setCreating(false)
    }
  }, [
    address,
    chadAddress,
    buyAmount,
    tokenSymbol,
    tokenAddress,
    contractAddress,
    chainId,
    apiUrl,
    coinNames,
    refAddress,
    switchChain,
    switchError,
    currentChainId,
    isLegacyV2,
    isV4,
    normalizedVersion
  ])

  const sanitizedLinks = useMemo(
    () => ({
      twitter: twitter?.startsWith('http')
        ? twitter
        : twitter
        ? `https://${twitter}`
        : undefined,
      telegram: telegram?.startsWith('http')
        ? telegram
        : telegram
        ? `https://${telegram}`
        : undefined,
      website: website?.startsWith('http')
        ? website
        : website
        ? `https://${website}`
        : undefined
    }),
    [twitter, telegram, website]
  )

  const isFav = useMemo(() => {
    const addr = String(contractAddress || '').toLowerCase()
    const key = `${addr}:${chainId ?? ''}`
    return (
      Array.isArray(watchlist) &&
      (watchlist.includes(key) || watchlist.includes(addr))
    )
  }, [watchlist, contractAddress, chainId])

  const onKeyOpen = useCallback(
    e => {
      if (!cardDisabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        history.push(navigateTo)
      }
    },
    [cardDisabled, history, navigateTo]
  )

  return (
    <div
      onClick={() => {
        if (!cardDisabled) history.push(navigateTo)
      }}
      role="button"
      tabIndex={cardDisabled ? -1 : 0}
      aria-disabled={cardDisabled}
      onKeyDown={onKeyOpen}
      className={`group rounded-md border-[0.5px] border-[#2a2d36] bg-[#171a21] text-[#f8ffe8] px-2 md:pr-3 md:pl-2 py-2.5 transition-all shadow-[0_0_8px_rgba(245,0,255,0.12)] hover:shadow-[0_0_12px_rgba(245,0,255,0.2)]${
        cardDisabled
          ? ' cursor-not-allowed opacity-70'
          : ' cursor-pointer hover:bg-[#1e2128] hover:border-[#383c46]'
      } relative overflow-hidden hover:shadow-[inset_0_0_0_1px_rgba(245,0,255,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a135bb] transform-gpu transition-transform duration-150 ease-out hover:scale-[1.005] hover:-translate-y-[1px] ${
        recentBuzz ? ' hk-new' : ''
      }`}
      style={{
        boxShadow: 'inset 0 0 0 1px transparent',
        transition: 'box-shadow 0.3s ease'
      }}
    >
      <div className="grid grid-cols-[64px_1fr] md:grid-cols-[72px_1fr] gap-3 text-sm items-stretch">
        {/* Left: big square logo column */}
        <div className="flex items-stretch">
          <div className="relative w-[64px] md:w-[72px] h-full rounded-[5px] overflow-hidden shrink-0 bg-black/30 flex items-center justify-center transition-transform duration-150 ease-out group-hover:scale-[1.02]">
            <TokenLogoImage
              address={chadAddress}
              chainId={chainId}
              version={normalizedVersion}
              src={resolvedLogoUrl}
              alt="Logo"
              loading="lazy"
              decoding="async"
              width={96}
              height={96}
              className="w-full h-full object-cover object-center"
            />
            {(resolvedChainLogoUrl || BlockchainLogo) && (
              <span className="absolute bottom-0.5 right-1 z-20 w-5 h-5 md:w-5 md:h-5 rounded-full border-[0.5px] border-[#383c46] overflow-hidden pointer-events-none">
                {resolvedChainLogoUrl ? (
                  <img
                    src={resolvedChainLogoUrl}
                    loading="lazy"
                    decoding="async"
                    width={24}
                    height={24}
                    alt="chain"
                    className="w-full h-full object-cover object-center"
                  />
                ) : (
                  <span className="w-full h-full flex items-center justify-center">
                    {BlockchainLogo}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Right: header + divider + footer stacked */}
        <div className="flex flex-col w-full gap-2 min-w-0">
          {/* Header Row */}
          <div className="grid grid-cols-[1fr_auto] min-w-0 items-start">
            <div className="flex flex-col min-w-0">
              <div className="min-w-0 flex items-center gap-2">
                <span className="shrink-0 inline-flex items-center rounded-[6px] bg-[#f500ff]/15 text-[#f500ff] text-[12px] font-semibold px-1.5 py-[1px]">
                  {tokenSymbol}
                </span>
                <span
                  className="block flex-1 min-w-0 text-[#E1E5D8]/80 text-xs whitespace-nowrap overflow-hidden text-ellipsis"
                  title={tokenName}
                >
                  {tokenName}
                </span>
              </div>

              <div className="mt-1">
                <div
                  className="w-full text-[12px] text-[#E1E5D8]/70 leading-[18px] h-[36px] flex items-start"
                  title={desc || ''}
                  aria-label="token description"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {desc && desc.length > 0 ? desc : ' '}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 flex-shrink-0 ml-1.5">
              {lastAction && lastActionNumber > 0 && (
                <div className="inline-flex items-center gap-1 h-5 px-2 rounded-[6px] border border-[#2a2d36] bg-[#2a2d36]/30 text-[11px] opacity-60 group-hover:opacity-100 transition-opacity">
                  <img
                    src={lastAction === 'bought' ? ArrowUpIcon : ArrowDownIcon}
                    width={12}
                    height={12}
                    alt="arrow"
                    decoding="async"
                    className="w-[12px] h-[12px]"
                  />
                  <span
                    className={`${
                      lastAction === 'bought'
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}
                  >
                    {lastActionNumber}
                  </span>
                </div>
              )}
              <div className="flex flex-col items-center gap-1">
                <button
                  type="button"
                  aria-label="Buy token"
                  onClick={e => {
                    e.stopPropagation()
                    onTokenSwap()
                  }}
                  disabled={!buyAmount || lpCreated || isSwitching}
                  className="bg-[#2a2d36]/40 border border-[#a135bb] rounded px-2 py-1 hover:bg-[#2a2d36] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                >
                  {creating ? (
                    <ClipLoader color="#a135bb" size={16} />
                  ) : (
                    <img
                      src={ThunderIcon}
                      width={20}
                      height={20}
                      alt="thunder"
                      decoding="async"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Divider (right column only) */}
          <div className="mt-1 h-px w-full bg-white/5" />

          {/* Footer Row (right column only) */}
          {/* Two lines × two columns layout */}
          <div className="grid grid-cols-[1.15fr_0.85fr] gap-y-0 items-start content-center w-full text-[12px] text-[#E1E5D8]/80 pt-1">
            {/* Row 1, Col 1: badges (time, holders %, dev %, progress%) */}
            <div className="flex gap-1 items-center whitespace-nowrap overflow-hidden justify-start self-center min-w-0">
              <span className="shrink-0 inline-flex items-center gap-1 h-[18px] px-1 rounded-[6px] border border-white/5 bg-transparent text-[11px] leading-[18px] tracking-tight text-[#E1E5D8]/75 font-medium">
                {timeAgo}
              </span>
              {top15HoldersPercent != null && (
                <span className="shrink-0 inline-flex items-center gap-1 h-[18px] px-1 rounded-[6px] border border-white/5 bg-transparent text-[11px] leading-[18px] tracking-tight text-[#E1E5D8]/75 font-medium">
                  <img
                    src={UserIcon}
                    alt="user"
                    className="w-[12px] h-[12px] opacity-80"
                    width={12}
                    height={12}
                    decoding="async"
                  />
                  {Math.floor(top15HoldersPercent)}%
                </span>
              )}
              {devPercent != null && (
                <span className="shrink-0 inline-flex items-center gap-1 h-[18px] px-1 rounded-[6px] border border-white/5 bg-transparent text-[11px] leading-[18px] tracking-tight text-[#E1E5D8]/75 font-medium">
                  <img
                    src={ChefIcon}
                    alt="chef"
                    className="w-[12px] h-[12px] opacity-80"
                    width={12}
                    height={12}
                    decoding="async"
                  />
                  {Math.floor(devPercent)}%
                </span>
              )}
              <span className="shrink-0 inline-flex items-center gap-1 h-[18px] px-1 rounded-[6px] border border-white/5 bg-transparent text-[11px] leading-[18px] tracking-tight text-[#E1E5D8]/75 font-medium">
                <img
                  src={ChartIcon}
                  alt="chart"
                  className="w-[12px] h-[12px] opacity-80"
                  width={12}
                  height={12}
                  decoding="async"
                />
                {Math.floor(progress)}%
              </span>
            </div>

            {/* Row 1, Col 2: values (volume & market cap numbers only) */}
            <div
              className={`grid grid-cols-2 gap-0 justify-items-end self-center -mr-1 transition-opacity ${
                creating || justUpdated ? 'opacity-60' : ''
              }`}
            >
              <span
                className={`min-w-[80px] text-right inline-flex items-center justify-end gap-1 ${
                  isRising ? 'text-green-400' : 'text-red-400'
                } font-semibold tabular-nums tracking-tight leading-tight leading-[18px]`}
              >
                ${formatMoney1(depositedAmount)}
              </span>
              <span
                className={`min-w-[80px] text-right inline-flex items-center justify-end gap-1 ${
                  isRising ? 'text-green-400' : 'text-red-400'
                } font-semibold tabular-nums tracking-tight leading-tight leading-[18px]`}
              >
                ${formatMoney1(marketCap)}
              </span>
            </div>

            {/* Row 2, Col 1: socials + holders + favorite (star) */}
            <div
              className="flex items-center gap-2 whitespace-nowrap self-center"
              onClick={e => e.stopPropagation()}
            >
              {website && (
                <a
                  href={sanitizedLinks.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open website"
                >
                  <img
                    src={WebsiteIcon}
                    alt="website"
                    className="w-[12px] h-[12px] opacity-70 hover:opacity-100 transition-opacity"
                    width={12}
                    height={12}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              )}
              {twitter && (
                <a
                  href={sanitizedLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Twitter"
                >
                  <img
                    src={TwitterIcon}
                    alt="twitter"
                    className="w-[12px] h-[12px] opacity-70 hover:opacity-100 transition-opacity"
                    width={12}
                    height={12}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              )}
              {telegram && (
                <a
                  href={sanitizedLinks.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Telegram"
                >
                  <img
                    src={TelegramIcon}
                    alt="telegram"
                    className="w-[12px] h-[12px] opacity-70 hover:opacity-100 transition-opacity"
                    width={12}
                    height={12}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              )}
              {holdersNumber != null && (
                <span className="flex items-center gap-1 tabular-nums leading-[18px]">
                  <img
                    src={UsersIcon}
                    alt="user"
                    className="w-[12px] h-[12px] opacity-80"
                    width={12}
                    height={12}
                    decoding="async"
                  />
                  {formatMoney1(holdersNumber)}
                </span>
              )}
              <button
                type="button"
                aria-pressed={isFav}
                aria-label={
                  isFav ? 'Remove from watchlist' : 'Add to watchlist'
                }
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleWatchlist(contractAddress, chainId)
                }}
                className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#2a2d36]/60 transition-colors"
              >
                <img
                  src={isFav ? StarIcon : StarlineIcon}
                  alt="star"
                  className="w-[12px] h-[12px] opacity-70 hover:opacity-100 transition-opacity"
                  width={12}
                  height={12}
                  decoding="async"
                />
              </button>
            </div>

            {/* Row 2, Col 2: value labels aligned under numbers */}
            <div className="grid grid-cols-2 gap-0 justify-items-end self-center -mr-1">
              <span className="min-w-[80px] text-[10px] leading-[10px] text-[#E1E5D8]/90 text-right">
                volume
              </span>
              <span className="min-w-[80px] text-[10px] leading-[10px] text-[#E1E5D8]/90 text-right">
                market cap
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(LaunchpadCard)
